import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '@/shared/api';
import { cleaningFailure } from '../cleaningFailure';
import { compressCleaningPhoto } from './compressCleaningPhoto';
import { createSlotUploadGuard, DuplicateSlotUploadError } from './slotUploadGuard';

export interface SlotUploadVars {
  itemId: string;
  file: File;
  /** Füllt dieser Upload den letzten offenen Slot? (aus dem Pre-Upload-Zustand berechnet) */
  willComplete: boolean;
  /** Falls willComplete: gibt es eine offene Meldung → Server setzt Issue statt Completed. */
  willBeIssue: boolean;
}

const withoutItem = (current: ReadonlySet<string>, itemId: string) => {
  const next = new Set(current);
  next.delete(itemId);
  return next;
};

/**
 * Foto je Raum hochladen und neu aufnehmen. Auto-Complete passiert server-seitig beim Upload; das
 * Feedback wird aus dem Pre-Upload-Zustand abgeleitet — der Upload liefert nur das Foto. Ohne
 * Antwort ist offen, ob das Foto ankam: dann markiert die Kachel nichts als gescheitert, der Stand
 * lädt neu (er kann die Aufgabe schon erledigt zeigen).
 */
export function useSlotCapture(taskId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const uploadGuard = useRef(createSlotUploadGuard());
  const [uploadingItemIds, setUploadingItemIds] = useState<ReadonlySet<string>>(new Set());
  const [uploadErrorItemIds, setUploadErrorItemIds] = useState<ReadonlySet<string>>(new Set());
  const invalidate = () => qc.invalidateQueries({ queryKey: ['cleaning'] });

  const uploadToSlot = useMutation({
    mutationFn: (vars: SlotUploadVars) =>
      uploadGuard.current.run(vars.itemId, async () => {
        const normalized = await compressCleaningPhoto(vars.file);
        return api.cleaning.uploadPhoto(taskId, normalized, 'ChecklistEvidence', vars.itemId, null);
      }),
    networkMode: 'always',
    onMutate: (vars) => {
      setUploadingItemIds((current) => new Set(current).add(vars.itemId));
      setUploadErrorItemIds((current) => withoutItem(current, vars.itemId));
    },
    onSuccess: (_photo, vars) => {
      toast.success(
        !vars.willComplete
          ? 'Foto gespeichert'
          : vars.willBeIssue
            ? 'Alle Fotos da – erledigt mit offener Meldung'
            : 'Alle Fotos da – Aufgabe erledigt',
      );
    },
    onError: async (error, vars) => {
      if (error instanceof DuplicateSlotUploadError) return;
      const failure = await cleaningFailure(error, 'Foto nicht gespeichert.');
      if (!failure.unknown) setUploadErrorItemIds((current) => new Set(current).add(vars.itemId));
      toast.error(failure.text);
    },
    onSettled: (_data, error, vars) => {
      if (error instanceof DuplicateSlotUploadError) return;
      setUploadingItemIds((current) => withoutItem(current, vars.itemId));
      void invalidate();
    },
  });

  const retakeSlot = useMutation({
    mutationFn: (photoId: string) => api.cleaning.deletePhoto(taskId, photoId),
    networkMode: 'always',
    onSuccess: () => toast.success('Foto entfernt – nimm den Raum neu auf'),
    onError: async (error) =>
      toast.error((await cleaningFailure(error, 'Foto nicht entfernt.')).text),
    onSettled: () => void invalidate(),
  });

  return { uploadToSlot, uploadingItemIds, uploadErrorItemIds, retakeSlot };
}
